import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { SKIP_GLOBAL_LOADER } from '../../../../utilities/services/loading/skip-global-loader.context';
import { GeoPoint, isValidLatLng, parseFiniteNumber } from './event-map-geo';

interface NominatimSearchHit {
  readonly lat?: string;
  readonly lon?: string;
}

interface GoogleGeocodeResponse {
  readonly status?: string;
  readonly results?: readonly {
    readonly geometry?: {
      readonly location?: {
        readonly lat?: number;
        readonly lng?: number;
      };
    };
  }[];
}

interface AppEnvironment {
  readonly googleMapsApiKey?: string;
}

const SESSION_CACHE_KEY = 'stupa-event-geocode-cache-v2';
const GOOGLE_GEOCODE_URL = 'https://maps.googleapis.com/maps/api/geocode/json';
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

@Injectable({ providedIn: 'root' })
export class EventPlaceGeocodeService {
  private readonly http = inject(HttpClient);
  private readonly mapsKey =
    (environment as AppEnvironment).googleMapsApiKey?.trim() ?? '';
  private readonly memory = new Map<string, GeoPoint | null>();

  constructor() {
    this.hydrateFromSession();
  }

  geocode(place: string): Observable<GeoPoint | null> {
    const query = place.trim();
    if (!query) {
      return of(null);
    }
    const cached = this.memory.get(query.toLowerCase());
    if (cached !== undefined) {
      return of(cached);
    }

    return this.mapsKey
      ? this.geocodeWithGoogle(query)
      : this.geocodeWithNominatim(query);
  }

  private geocodeWithGoogle(query: string): Observable<GeoPoint | null> {
    const params = new HttpParams()
      .set('address', query)
      .set('key', this.mapsKey);

    return this.http.get<GoogleGeocodeResponse>(GOOGLE_GEOCODE_URL, {
      params,
      context: this.quietContext(),
    }).pipe(
      map((body) => this.cacheResult(query, this.parseGoogle(body))),
      catchError(() => this.geocodeWithNominatim(query)),
    );
  }

  private geocodeWithNominatim(query: string): Observable<GeoPoint | null> {
    const params = new HttpParams()
      .set('format', 'json')
      .set('limit', '1')
      .set('q', query);

    return this.http.get<NominatimSearchHit[]>(NOMINATIM_URL, {
      params,
      context: this.quietContext(),
    }).pipe(
      map((hits) => this.cacheResult(query, this.parseNominatim(hits[0]))),
      catchError(() => of(this.cacheResult(query, null))),
    );
  }

  private parseGoogle(body: GoogleGeocodeResponse | null): GeoPoint | null {
    if (body?.status !== 'OK') {
      return null;
    }
    const location = body.results?.[0]?.geometry?.location;
    const lat = parseFiniteNumber(location?.lat);
    const lng = parseFiniteNumber(location?.lng);
    if (lat == null || lng == null || !isValidLatLng(lat, lng)) {
      return null;
    }
    return { lat, lng };
  }

  private parseNominatim(hit: NominatimSearchHit | undefined): GeoPoint | null {
    if (!hit?.lat || !hit?.lon) {
      return null;
    }
    const lat = Number(hit.lat);
    const lng = Number(hit.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !isValidLatLng(lat, lng)) {
      return null;
    }
    return { lat, lng };
  }

  private quietContext(): HttpContext {
    return new HttpContext().set(SKIP_GLOBAL_LOADER, true);
  }

  private cacheResult(query: string, point: GeoPoint | null): GeoPoint | null {
    const key = query.toLowerCase();
    this.memory.set(key, point);
    this.persistSession();
    return point;
  }

  private hydrateFromSession(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
      if (!raw) {
        return;
      }
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') {
        return;
      }
      for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
        const record = value as GeoPoint | null;
        if (
          record &&
          typeof record === 'object' &&
          Number.isFinite(record.lat) &&
          Number.isFinite(record.lng)
        ) {
          this.memory.set(key, { lat: record.lat, lng: record.lng });
        } else if (record === null) {
          this.memory.set(key, null);
        }
      }
    } catch {
      this.memory.clear();
    }
  }

  private persistSession(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      const payload: Record<string, GeoPoint | null> = {};
      this.memory.forEach((value, key) => {
        payload[key] = value;
      });
      sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(payload));
    } catch {
      // Quota or private-mode — skip persist.
    }
  }
}
